import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { LogoutFunc } from "../store/Slice/AuthSlice";
import { useGetUserProfileQuery } from "../store/api/authApi";

const UserAccount = () => {
  const dispatch = useDispatch();

  const {
    data: userProfile,
    isLoading,
    isError,
    refetch,
  } = useGetUserProfileQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    refetch();
  }, [refetch]);

  const {
    user: reduxUser,
    phoneNumber: reduxPhone,
    email: reduxEmail,
    gender: reduxGender,
    dateOfBirth: reduxDOB,
  } = useSelector((state) => state.auth);

  const firstName =
    userProfile?.firstName ||
    (Array.isArray(reduxUser) ? reduxUser[0] : reduxUser) ||
    "User";
  const lastName = userProfile?.lastName || "";
  const name = lastName ? `${firstName} ${lastName}` : firstName;
  const phoneNumber = userProfile?.phoneNumber || reduxPhone;
  const email = userProfile?.email || reduxEmail;
  const gender = reduxGender;
  const dateOfBirth = reduxDOB;
  const userId = userProfile?.id;

  const getActiveSubscription = () => {
    if (!userProfile?.subscriptions || userProfile.subscriptions.length === 0) {
      return null;
    }

    const now = new Date();

    const activeSubscriptions = [...userProfile.subscriptions]
      .filter((sub) => new Date(sub.expiryAt) > now)
      .sort((a, b) => new Date(b.expiryAt) - new Date(a.expiryAt));

    return (
      activeSubscriptions[0] ||
      userProfile.subscriptions[userProfile.subscriptions.length - 1]
    );
  };

  const activeSubscription = getActiveSubscription();
  const isSubscriptionActive =
    activeSubscription && new Date(activeSubscription.expiryAt) > new Date();

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const navigate = useNavigate();
  const handleLogout = () => {
    dispatch(LogoutFunc());
    navigate("/");
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name.charAt(0).toUpperCase();
  };

  const getAvatarColor = (name) => {
    const colors = [
      "bg-blue-500",
      "bg-green-500",
      "bg-purple-500",
      "bg-pink-500",
      "bg-indigo-500",
      "bg-red-500",
      "bg-yellow-500",
      "bg-teal-500",
    ];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  const hasPersonalInfo = email || gender || dateOfBirth;

  return (
    <div className="w-full mx-auto p-4 lg:p-6 max-w-6xl min-h-screen">
      {isLoading && (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
            <p className="text-gray-600 font-medium">Loading your profile...</p>
          </div>
        </div>
      )}

      {isError && !isLoading && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-6 mb-6">
          <div className="flex items-center gap-3">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div>
              <p className="text-red-800 font-medium">
                Failed to load profile data
              </p>
              <p className="text-red-600 text-sm">
                Using cached data. Please refresh to try again.
              </p>
            </div>
          </div>
        </div>
      )}

      {!isLoading && (
        <>
          <div className="w-full">
            <div className="bg-white border border-[#D9D9D9] rounded-xl shadow-sm mb-6">
              <div className="px-6 py-5">
                <h2 className="text-xl font-bold text-[#454545]">My Profile</h2>
              </div>
            </div>
          </div>

          {hasPersonalInfo && (
            <div className="bg-white border border-[#D9D9D9] rounded-2xl shadow-sm mb-6">
              <div className="px-6 py-6">
                <div className="flex items-start gap-4 mb-6">
                  <div
                    className={`w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl font-bold ${getAvatarColor(
                      name
                    )}`}
                  >
                    {getInitials(name)}
                  </div>
                  <div>
                    <h3 className="text-lg text-[#1F2937] font-semibold capitalize">
                      {name}
                    </h3>
                    {phoneNumber && (
                      <p className="text-sm text-[#9D9D9D] mt-1">
                        +91-{phoneNumber}
                      </p>
                    )}
                  </div>
                </div>
                <div className="px-5 py-5 border-t border-[#D9D9D9]">
                  <h4 className="text-lg font-semibold text-[#454545] text-roboto">
                    Personal Information
                  </h4>
                </div>

                <div className="px-5 py-2">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center justify-between">
                        <label className="text-base font-roboto font-normal text-[#454545]">
                          Name
                        </label>
                      </div>
                      <input
                        readOnly
                        value={name}
                        className="w-full text-base border border-[#D9D9D9] rounded-lg px-3 py-2.5 bg-white text-[#454545] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    {dateOfBirth && (
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center justify-between">
                          <label className="text-base font-roboto font-normal text-[#454545]">
                            D.O.B
                          </label>
                        </div>
                        <input
                          readOnly
                          value={dateOfBirth}
                          className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    )}

                    {email && (
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center justify-between">
                          <label className="text-base font-roboto font-normal text-[#454545]">
                            Email ID
                          </label>
                        </div>
                        <input
                          readOnly
                          value={email}
                          className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    )}

                    {gender && (
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center justify-between">
                          <label className="text-base font-roboto font-normal text-[#454545]">
                            Gender
                          </label>
                        </div>
                        <input
                          readOnly
                          value={gender}
                          className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2 pt-8 border-t border-[#D9D9D9] mt-6 mb-6">
                    <h4 className="text-lg font-roboto font-semibold text-[#454545] ">
                      Subscription
                    </h4>
                    {activeSubscription && (
                      <span
                        className={`inline-block text-xs px-2.5 py-1 rounded-full font-medium ${
                          isSubscriptionActive
                            ? "bg-[#1E8C45] text-white"
                            : "bg-[#FF4D4F] text-white"
                        }`}
                      >
                        {isSubscriptionActive ? "Active" : "Expired"}
                      </span>
                    )}
                  </div>

                  {isLoading ? (
                    <div className="text-center py-8">
                      <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      <p className="text-gray-500 mt-2">
                        Loading subscription details...
                      </p>
                    </div>
                  ) : userProfile?.subscriptions &&
                    userProfile.subscriptions.length > 0 ? (
                    <div className="space-y-6">
                      {[...userProfile.subscriptions]
                        .sort(
                          (a, b) => new Date(b.expiryAt) - new Date(a.expiryAt)
                        )
                        .map((subscription, index) => {
                          const isActive =
                            new Date(subscription.expiryAt) > new Date();
                          return (
                            <div
                              key={index}
                              className="grid grid-cols-1 md:grid-cols-2 gap-6"
                            >
                              <div>
                                <p className="text-base font-medium text-[#454545] mb-2">
                                  Advisor Name
                                </p>
                                <p className="text-base text-[#9D9D9D] font-roboto">
                                  {subscription.advisorName || "N/A"}
                                </p>
                              </div>
                              <div>
                                <p className="text-base font-medium text-[#454545] mb-2">
                                  Subscription Status
                                </p>
                                <p className="text-base text-[#9D9D9D] font-roboto">
                                  {isActive ? "Active Plan" : "Expired"}
                                </p>
                              </div>
                              <div>
                                <p className="text-base font-medium text-[#454545] mb-2">
                                  Expiry Date
                                </p>
                                <p className="text-base text-[#9D9D9D] font-roboto">
                                  {formatDate(subscription.expiryAt)}
                                </p>
                              </div>
                              <div>
                                <p className="text-base font-medium text-[#454545] mb-2">
                                  Subscription ID
                                </p>
                                <p className="text-base text-[#9D9D9D] font-roboto truncate">
                                  {subscription.id || "N/A"}
                                </p>
                              </div>
                              {index < userProfile.subscriptions.length - 1 && (
                                <div className="col-span-1 md:col-span-2 border-b border-[#D9D9D9] my-2"></div>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-16 w-16 mx-auto text-gray-300 mb-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      <p className="text-gray-500 font-medium">
                        No Active Subscription
                      </p>
                      <p className="text-gray-400 text-sm mt-1">
                        Subscribe to an advisor to get started
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default UserAccount;
